import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { setGuestAllowed } from '../utils/guestMode';
import { clearAuthSession, getGlobalToken } from '../utils/authStorage';
import { migrateGuestDataToCloud } from '../utils/cloudMigration';
import { config } from '../config/env';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow};
  padding: 1rem;
`;

const Title = styled.h1`
  margin: 0 0 0.75rem;
  color: ${({ theme }) => theme.text};
`;

const SectionTitle = styled.h2`
  margin: 1.25rem 0 0.75rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.text};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 0.75rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.border};

  &:last-child {
    border-bottom: none;
  }
`;

const Key = styled.div`
  color: ${({ theme }) => theme.textSecondary};
`;

const Val = styled.div`
  color: ${({ theme }) => theme.text};
  font-weight: 600;
`;

const Actions = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.55rem 0.85rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Form = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  border-radius: 10px;
  padding: 0.65rem 0.8rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
  }
`;

const Message = styled.div`
  margin-top: 0.75rem;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
`;

function openAuthModal() {
  window.dispatchEvent(new CustomEvent('open-auth-modal'));
}

function validatePassword(password: string) {
  return /^(?=.*[a-z])(?=.*\d).{8,}$/.test(password);
}

async function postJson<T>(url: string, body?: unknown, token?: string): Promise<T> {
  const base = config.authBaseURL ?? '';
  const fullUrl = base ? `${base.replace(/\/$/, '')}${url}` : url;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(fullUrl, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  let payload: any = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    throw new Error(payload?.message || payload?.error || `HTTP ${res.status}`);
  }

  return payload as T;
}

export default function Account() {
  const token = localStorage.getItem('today_token');
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem('today_user');
      return raw ? (JSON.parse(raw) as any) : null;
    } catch {
      return null;
    }
  }, []);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isLoggedIn = !!token && !!user?.id;

  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please complete all password fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The new password fields do not match.');
      return;
    }
    if (!validatePassword(newPassword)) {
      setError('Password must be at least 8 characters and include one lowercase letter and one number.');
      return;
    }

    const globalToken = getGlobalToken();
    if (!globalToken) {
      setError('Your global sign-in token is missing. Please sign in again.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      await postJson('/auth/password/change', { currentPassword, newPassword }, globalToken);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage('Password updated successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>Account</Title>
      <Card>
        {!isLoggedIn ? (
          <>
            <div style={{ color: 'inherit', opacity: 0.8, lineHeight: 1.6 }}>
              You are not signed in yet. Sign in to enable cloud sync for Today items and guide data.
            </div>
            <Actions>
              <Button onClick={openAuthModal}>Login / Register</Button>
              <Button
                onClick={() => {
                  setGuestAllowed(true);
                  alert('Guest mode is active. Data will stay in this browser only.');
                }}
              >
                Continue as guest
              </Button>
            </Actions>
          </>
        ) : (
          <>
            <Row>
              <Key>Username</Key>
              <Val>{String(user.username ?? '')}</Val>
            </Row>
            <Row>
              <Key>Email</Key>
              <Val>{String(user.email ?? '')}</Val>
            </Row>
            <Row>
              <Key>Role</Key>
              <Val>{String(user.role ?? '')}</Val>
            </Row>
            <Row>
              <Key>User ID</Key>
              <Val>{String(user.id ?? '')}</Val>
            </Row>

            <SectionTitle>Change Password</SectionTitle>
            <div style={{ color: 'inherit', opacity: 0.8, lineHeight: 1.6 }}>
              Use this form when you know your current password. If you forgot it, use the forgot password flow from the login dialog.
            </div>
            <Form>
              <Input
                placeholder="Current password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <Input
                placeholder="New password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <Input
                placeholder="Confirm new password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Form>

            {message ? <Message>{message}</Message> : null}
            {error ? <Message>{error}</Message> : null}

            <Actions>
              <Button
                onClick={async () => {
                  const uid = Number(user.id);
                  const r = await migrateGuestDataToCloud(uid);
                  alert(
                    `Import finished: today=${r.today}, guides created=${r.guidesCreated}, guides updated=${r.guidesUpdated}` +
                      (r.guidesFailed ? `, guides failed=${r.guidesFailed}` : ''),
                  );
                }}
                title="Import guest Today and guide data into the cloud account."
              >
                Import local data
              </Button>
              <Button onClick={() => void changePassword()} disabled={submitting}>
                {submitting ? 'Updating password...' : 'Change password'}
              </Button>
              <Button
                onClick={() => {
                  clearAuthSession();
                  setGuestAllowed(true);
                  alert('You have been signed out and switched back to guest mode.');
                  window.location.href = '/';
                }}
              >
                Sign out
              </Button>
            </Actions>
          </>
        )}
      </Card>
    </Container>
  );
}
